import { useEffect, useState } from 'react';
import { EnterpriseLayout } from '../components/EnterpriseLayout';


// ─── Local Ad types & components ───────────────────
interface AdCreative {
  id: string;
  title: string;
  description: string;
  sponsor: string;
  ctaLabel: string;
  href: string;
  badge: string;
  background: string;
  imageUrl: string;
}

function BannerAd({ creative }: { creative: AdCreative }) {
  return (
    <a
      href={creative.href}
      className="relative block overflow-hidden rounded-2xl shadow-sm transition-shadow hover:shadow-md"
      style={{ background: creative.background }}
    >
      <div className="relative z-10 p-6 text-white">
        <div className="mb-1">
          <span className="inline-block rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold backdrop-blur-sm">
            {creative.badge}
          </span>
        </div>
        <h3 className="text-lg font-bold">{creative.title}</h3>
        <p className="mt-1 text-sm text-white/80">{creative.description}</p>
        <div className="mt-3 flex items-center gap-2">
          <span className="rounded-lg bg-white/20 px-4 py-1.5 text-sm font-semibold backdrop-blur-sm">
            {creative.ctaLabel}
          </span>
          <span className="text-xs text-white/60">{creative.sponsor}</span>
        </div>
      </div>
    </a>
  );
}

function StartupAdOverlay({ open, creative, countdownSeconds, onClose }: {
  open: boolean;
  creative: AdCreative;
  countdownSeconds: number;
  onClose: () => void;
}) {
  const [countdown, setCountdown] = useState(countdownSeconds);

  useEffect(() => {
    if (!open) return;
    setCountdown(countdownSeconds);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [open, countdownSeconds, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative max-w-md w-full mx-4 overflow-hidden rounded-3xl shadow-2xl"
        style={{ background: creative.background }}>
        <div className="p-6 text-white text-center">
          <div className="mb-2">
            <span className="inline-block rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold">
              {creative.badge}
            </span>
          </div>
          <h2 className="text-xl font-bold">{creative.title}</h2>
          <p className="mt-2 text-sm text-white/80">{creative.description}</p>
          <p className="mt-1 text-xs text-white/60">{creative.sponsor}</p>
          <a
            href={creative.href}
            className="mt-4 inline-block rounded-xl bg-white/20 px-6 py-2.5 text-sm font-semibold backdrop-blur-sm"
          >
            {creative.ctaLabel}
          </a>
        </div>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 rounded-full bg-black/20 px-3 py-1 text-xs text-white backdrop-blur-sm"
        >
          跳过 {countdown}s
        </button>
      </div>
    </div>
  );
}

// ─── Dashboard page ────────────────────────────────
interface DashboardStats {
  pendingDispatch: string;
  activeWorkers: string;
  abnormalAttendance: string;
  completionRate: string;
}

export default function Dashboard() {
  const [adData, setAdData] = useState<AdCreative | null>(null);
  const [showAd, setShowAd] = useState(false);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const mockStats: DashboardStats = {
          pendingDispatch: "42",
          activeWorkers: "128",
          abnormalAttendance: "3",
          completionRate: "94%",
        };

        const mockResponse: AdCreative = {
          id: 'dashboard-release-notice',
          title: "数字化零工调度新引擎",
          description: "早知 2.0 版本现已发布，增强了 AI 智能匹配算法与声纹活体检测。建议立即更新体验。",
          sponsor: "早知科技 (ZHAOZHI TECH)",
          ctaLabel: "立即体验",
          href: "/jobs",
          badge: "官方发布",
          background: "linear-gradient(135deg, #6366f1, #a855f7)",
          imageUrl: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2070&auto=format&fit=crop"
        };

        setTimeout(() => {
          setStats(mockStats);
          setAdData(mockResponse);
          setShowAd(true);
        }, 800);
      } catch (error) {
        console.error("接口连接失败:", error);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <EnterpriseLayout>
      <section className="space-y-2">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">数据概览</h1>
        <p className="text-slate-500 text-lg">欢迎回来，这是今天零工调度的实时监控结果。</p>
      </section>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {stats ? [
          { label: "今日待调度", value: stats.pendingDispatch, trend: "+5", color: "text-indigo-600" },
          { label: "在岗人员", value: stats.activeWorkers, trend: "正常", color: "text-green-600" },
          { label: "异常考勤", value: stats.abnormalAttendance, trend: "-12%", color: "text-red-600" },
          { label: "完成率", value: stats.completionRate, trend: "+2%", color: "text-indigo-600" },
        ].map((item, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-hover hover:shadow-md">
            <p className="text-sm font-medium text-slate-500">{item.label}</p>
            <div className="mt-2 flex items-baseline justify-between">
              <span className={`text-3xl font-bold ${item.color}`}>{item.value}</span>
              <span className="text-xs font-semibold text-slate-400">{item.trend}</span>
            </div>
          </div>
        )) : (
          Array(4).fill(0).map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-2xl bg-slate-100 border border-slate-100" />
          ))
        )}
      </div>

      {adData && <BannerAd creative={adData} />}

      {adData && (
        <StartupAdOverlay
          open={showAd}
          creative={adData}
          countdownSeconds={5}
          onClose={() => setShowAd(false)}
        />
      )}
    </EnterpriseLayout>
  );
}
